import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
	useGetMonthStats,
	useGetTimereportTypes,
	useGetWorkSchedule,
	useGetUserInfo,
	useGetHolidays,
} from '../../../Core/Hooks/useTimereport';
import useWindowSize from '../../../Core/Hooks/useWindowSize';
import './MonthlyReports.scss';

type MonthlyReportsProps = {
	year: number;
};

const formatHours = (value: number): string => {
	return value % 1 === 0 ? String(value) : value.toFixed(2).replace(/0$/, '');
};

const getWorkingDaysInMonth = (year: number, month: number): number => {
	const daysInMonth = new Date(year, month + 1, 0).getDate();
	let count = 0;
	for (let d = 1; d <= daysInMonth; d++) {
		const day = new Date(year, month, d).getDay();
		if (day !== 0 && day !== 6) count++;
	}
	return count;
};

const MonthlyReports = ({ year }: MonthlyReportsProps) => {
	const { t, i18n } = useTranslation();
	const from = `${year}-01-01`;
	const to = `${year}-12-31`;
	const { monthStats } = useGetMonthStats(from, to);
	const { types } = useGetTimereportTypes();
	const { workSchedule } = useGetWorkSchedule();
	const { userInfo } = useGetUserInfo();
	const { holidays } = useGetHolidays();
	const { isMobile } = useWindowSize();
	const locale = i18n.resolvedLanguage?.startsWith('sv') ? 'sv-SE' : 'en-GB';

	const monthFormatter = useMemo(() => new Intl.DateTimeFormat(locale, { month: 'long' }), [locale]);

	const holidayHoursByMonth = useMemo(() => {
		const hours = new Array(12).fill(0);
		holidays
			?.filter((h) => !h.isDeleted && h.date.startsWith(String(year)))
			.forEach((h) => {
				const monthIndex = new Date(h.date).getMonth();
				hours[monthIndex] += h.hours;
			});
		return hours;
	}, [holidays, year]);

	const dailyScheduledHours = useMemo(() => {
		if (workSchedule) {
			const weekdays = [
				workSchedule.hoursMonday,
				workSchedule.hoursTuesday,
				workSchedule.hoursWednesday,
				workSchedule.hoursThursday,
				workSchedule.hoursFriday,
			];
			return weekdays.reduce((sum, h) => sum + h, 0) / 5;
		}
		if (userInfo?.normalWeekHours) {
			return userInfo.normalWeekHours / 5;
		}
		return 0; //need to support zero scheduled hours
		//return 8;
	}, [workSchedule, userInfo?.normalWeekHours]);

	const activeTypes = useMemo(() => {
		return types?.filter((t) => !t.isDeleted && t.showInStatsList) ?? [];
	}, [types]);

	const monthlyData = useMemo(() => {
		const data = Array.from({ length: 12 }, (_, index) => ({
			month: monthFormatter.format(new Date(year, index, 1)),
			monthIndex: index,
			standardizedHours: Math.max(0, getWorkingDaysInMonth(year, index) * dailyScheduledHours - holidayHoursByMonth[index]),
			totalHours: 0,
			totalHoursTime: 0,
			totalHoursMoney: 0,
			hoursByType: new Map<number, number>(),
			entries: 0,
		}));

		monthStats
			?.filter((s) => !s.isDeleted)
			.forEach((s) => {
				const monthIndex = new Date(s.dateFrom).getMonth();
				data[monthIndex].totalHours += s.hours;
				data[monthIndex].totalHoursTime += s.hoursTime;
				data[monthIndex].totalHoursMoney += s.hoursMoney;
				data[monthIndex].entries += 1;
				const prev = data[monthIndex].hoursByType.get(s.typeId) ?? 0;
				data[monthIndex].hoursByType.set(s.typeId, prev + s.hours);
			});

		return data.filter((d) => d.entries > 0);
	}, [monthStats, year, dailyScheduledHours, holidayHoursByMonth, monthFormatter]);

	const visibleTypes = useMemo(() => {
		return activeTypes.filter((t) => monthlyData.some((d) => (d.hoursByType.get(t.id) ?? 0) > 0));
	}, [activeTypes, monthlyData]);

	if (isMobile) {
		return (
			<div className='biz-monthly-reports'>
				<div className='biz-monthly-reports__grid'>
					{monthlyData.map((stat) => (
						<div key={stat.month} className='biz-monthly-reports__card'>
							<span className='biz-monthly-reports__month-name'>{stat.month}</span>
							{dailyScheduledHours > 0 && (
								<div className='biz-monthly-reports__stat'>
									<span className='biz-monthly-reports__stat-label'>
										{t('TIMEREPORT_USER.OVERVIEW.MONTHLY_REPORTS.STANDARDIZED')}
									</span>
									<span className='biz-monthly-reports__stat-value'>{formatHours(stat.standardizedHours)}</span>
								</div>
							)}
							{activeTypes
								.filter((t) => (stat.hoursByType.get(t.id) ?? 0) > 0)
								.map((t) => (
									<div key={t.id} className='biz-monthly-reports__stat'>
										<span className='biz-monthly-reports__stat-label'>{t.name}</span>
										<span className='biz-monthly-reports__stat-value'>
											{formatHours(stat.hoursByType.get(t.id) ?? 0)}
										</span>
									</div>
								))}
							<div className='biz-monthly-reports__stat'>
								<span className='biz-monthly-reports__stat-label'>
									{t('TIMEREPORT_USER.OVERVIEW.MONTHLY_REPORTS.TOTAL_HOURS')}
								</span>
								<span className='biz-monthly-reports__stat-value'>{formatHours(stat.totalHours)}</span>
							</div>
							{dailyScheduledHours > 0 && (
								<div className='biz-monthly-reports__stat'>
									<span className='biz-monthly-reports__stat-label'>
										{t('TIMEREPORT_USER.OVERVIEW.MONTHLY_REPORTS.HOURS_TIME')}
									</span>
									<span className='biz-monthly-reports__stat-value'>{formatHours(stat.totalHoursTime)}</span>
								</div>
							)}
							{dailyScheduledHours > 0 && (
								<div className='biz-monthly-reports__stat'>
									<span className='biz-monthly-reports__stat-label'>
										{t('TIMEREPORT_USER.OVERVIEW.MONTHLY_REPORTS.HOURS_MONEY')}
									</span>
									<span className='biz-monthly-reports__stat-value'>{formatHours(stat.totalHoursMoney)}</span>
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		);
	}

	return (
		<div className='biz-monthly-reports'>
			<table className='biz-monthly-reports__table'>
				<thead>
					<tr>
						<th>{t('TIMEREPORT_USER.OVERVIEW.MONTHLY_REPORTS.MONTH')}</th>
						{dailyScheduledHours > 0 && <th>{t('TIMEREPORT_USER.OVERVIEW.MONTHLY_REPORTS.STANDARDIZED')}</th>}
						{visibleTypes.map((t) => (
							<th key={t.id}>{t.name}</th>
						))}
						<th>{t('TIMEREPORT_USER.OVERVIEW.MONTHLY_REPORTS.TOTAL')}</th>
						{dailyScheduledHours > 0 && <th>{t('TIMEREPORT_USER.OVERVIEW.MONTHLY_REPORTS.SAVED_HOURS')}</th>}
						{dailyScheduledHours > 0 && <th>{t('TIMEREPORT_USER.OVERVIEW.MONTHLY_REPORTS.WITHDRAWAL_MONEY')}</th>}
					</tr>
				</thead>
				<tbody>
					{monthlyData.map((stat) => (
						<tr key={stat.month}>
							<td>{stat.month}</td>
							{dailyScheduledHours > 0 && <td>{formatHours(stat.standardizedHours)}</td>}
							{visibleTypes.map((t) => (
								<td key={t.id}>{formatHours(stat.hoursByType.get(t.id) ?? 0)}</td>
							))}
							<td>{formatHours(stat.totalHours)}</td>
							{dailyScheduledHours > 0 && <td>{formatHours(stat.totalHoursTime)}</td>}
							{dailyScheduledHours > 0 && <td>{formatHours(stat.totalHoursMoney)}</td>}
						</tr>
					))}
				</tbody>
				<tfoot>
					<tr>
						<td>{t('TIMEREPORT_USER.OVERVIEW.MONTHLY_REPORTS.TOTAL')}</td>
						{dailyScheduledHours > 0 && (
							<td>{formatHours(monthlyData.reduce((sum, d) => sum + d.standardizedHours, 0))}</td>
						)}
						{visibleTypes.map((t) => (
							<td key={t.id}>
								{formatHours(monthlyData.reduce((sum, d) => sum + (d.hoursByType.get(t.id) ?? 0), 0))}
							</td>
						))}
						<td>{formatHours(monthlyData.reduce((sum, d) => sum + d.totalHours, 0))}</td>
						{dailyScheduledHours > 0 && (
							<td>{formatHours(monthlyData.reduce((sum, d) => sum + d.totalHoursTime, 0))}</td>
						)}
						{dailyScheduledHours > 0 && (
							<td>{formatHours(monthlyData.reduce((sum, d) => sum + d.totalHoursMoney, 0))}</td>
						)}
					</tr>
				</tfoot>
			</table>
		</div>
	);
};

export default MonthlyReports;
